import { observer } from "mobx-react-lite"
import { MergeConflictInline } from "./MergeConflict"
import { Theme } from "../../../../misc/Theme"
import { store } from "../../../../stores/Store"
import { CheckIcon } from "../../../views/questionnaire/QuestionnaireTable"
import { formatFieldKey, formatValue, keysToIgnore, updatableFieldNames } from "./mergeUtils"
import { Icons } from "../../../../misc/Icons"
import React from "react"


interface MergeFieldProps {
    dataSources: any[];
    fieldKey: string;
    showConflict?: boolean;
}


const MergeField = observer(({dataSources, fieldKey, showConflict = false}: MergeFieldProps) => {

    const dataSourcesWithOverrides = [store.QuestionnaireStore.mergeOverride, ...dataSources]
    const releventDataSourceOptions = dataSources.filter(ds => fieldKey in ds).map(ds => {
        return {value: ds[fieldKey], label: ds.label, iconId: ds.iconId}
    })
    const dataSource = dataSourcesWithOverrides.find(ds => fieldKey in ds)
    const possibleValues = new Set(dataSources.filter(ds => fieldKey in ds).map(ds => {return ds[fieldKey]}))

    return (
        <div style={{display: "grid", gridTemplateColumns: "220px auto", padding: "4px 0px 4px 8px", gap: "15px", borderBottom: "0.5px solid #c5c5c5"}}>
            <div>{formatFieldKey(fieldKey)}</div>
            
            <div style={{display: "flex", flexWrap: "wrap", overflowWrap: "anywhere", gap: "10px"}}>
                {possibleValues?.size > 1 ? (
                    <MergeConflictInline dataSources={releventDataSourceOptions} fieldKey={fieldKey} />
                ) : (
                    <NoConflict dataSource={dataSource} fieldKey={fieldKey} />
                )}
            </div>
        </div>
    )
})


function NoConflict ({dataSource, fieldKey}) {
    
    const value = dataSource[fieldKey]
    const iconId = dataSource.iconId
    
    if (Array.isArray(value)) {
        return <MergeTableField table={value} fieldKey={fieldKey} iconId={iconId} />
    }

    const formattedValue = formatValue(fieldKey, value)

    return (
        <div style={{display: "flex", gap: "5px"}}>
            <div style={{margin: "2px 0 0 0"}}><CheckIcon index={iconId} /></div>
            
            {!updatableFieldNames.includes(fieldKey) ? (
                <span>{formattedValue ? formattedValue : <i className="font-grey">Empty</i>}</span>
            ) : (
                <input 
                    name={fieldKey} 
                    value={value} 
                    style={{border: "none", backgroundColor: "white", boxShadow: "0 0 10px 0 #d3d3d3", padding: "2px 4px", cursor: "text"}} 
                    onChange={e => {
                        store.QuestionnaireStore.setMergeOverride({...store.QuestionnaireStore.mergeOverride, [fieldKey]: e.target.value})
                    }} />
            )}
        </div>
    )
}


function MergeTableField ({table, fieldKey, iconId}) {
    return table.map((tableRow, i) => {
        if (typeof tableRow === "object" && "iconId" in tableRow) {
            iconId = tableRow.iconId
        }
        return (
            <React.Fragment key={i}>
                <div style={{width: "stretch", backgroundColor: "#d7d7d7", padding: "5px"}}>
                    <small>{formatFieldKey(fieldKey)} ({i + 1})</small>
                </div>

                <div style={{width: "stretch", borderLeft: "10px solid #d7d7d7", borderBottom: "5px solid #d7d7d7"}}>
                    {Object.keys(tableRow).filter(fieldKey => !keysToIgnore.includes(fieldKey)).map((fieldKey, i) => (
                        <MergeField key={i} dataSources={[{...tableRow, iconId: iconId}]} fieldKey={fieldKey} />
                    ))}
                </div> 
            </React.Fragment>
        )
    })
}

export default MergeField;